import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Tooltip,
  useMediaQuery,
  Theme,
} from '@material-ui/core';
import EventBusyIcon from '@material-ui/icons/EventBusy';
import * as React from 'react';
import { ReservationService } from 'services';
import { Reservation } from 'interfaces';
import { TableAvailabilityDialog } from '../TableAvailabilityDialog';

interface Props {
  reservation: Reservation | null;
  open: boolean;
  onClose: () => void;
  onApproved: () => void;
}

/**
 * Approve-with-table dialog. Replaces the raw status flip on the
 * Requests tab so we can't silently end up with an approved reservation
 * lacking a table assignment. Table is required (mirrors the server
 * rule `tableRequiredForApproved` in ReservationsTable).
 */
export const ApproveReservationDialog: React.FC<Props> = ({
  reservation,
  open,
  onClose,
  onApproved,
}) => {
  const [tableNames, setTableNames] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [availabilityOpen, setAvailabilityOpen] = React.useState(false);
  const isMobile = useMediaQuery((t: Theme) => t.breakpoints.down('sm'));

  React.useEffect(() => {
    if (open) {
      setTableNames(reservation?.tableNames || '');
      setError(null);
    }
  }, [open, reservation]);

  if (!reservation) return null;

  const reservedAt = new Date(reservation.reservedAt);
  const reservedAtLabel = isNaN(reservedAt.getTime())
    ? '—'
    : reservedAt.toLocaleString();

  const handleApprove = async () => {
    const cleaned = tableNames.trim();
    if (!cleaned) {
      setError('Please assign a table before approving.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await ReservationService.update(reservation.id, {
        status: 'approved',
        table_names: cleaned,
      });
      onApproved();
    } catch (e) {
      setError((e as Error)?.message || 'Failed to approve reservation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
    >
      <DialogTitle>Approve reservation</DialogTitle>
      <DialogContent>
        <Typography variant="body2" style={{ marginBottom: 12 }}>
          <strong>{reservation.name}</strong> · {reservation.partySize}{' '}
          {reservation.partySize === 1 ? 'guest' : 'guests'}
          <br />
          {reservedAtLabel}
          {reservation.experience && (
            <>
              <br />
              Experience: <strong>{reservation.experience.title}</strong>
            </>
          )}
        </Typography>

        <TextField
          autoFocus
          fullWidth
          label="Table (comma-separated for multi-table)"
          placeholder="e.g. 22 or 16, 1, 13"
          value={tableNames}
          onChange={(e) => setTableNames(e.target.value)}
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="See bookings already on this date">
                  <IconButton
                    size="small"
                    onClick={() => setAvailabilityOpen(true)}
                  >
                    <EventBusyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ display: 'block', marginTop: 6 }}
        >
          Approving sends the confirmation email to {reservation.email || 'the customer'}.
        </Typography>

        {error && (
          <Typography color="error" variant="body2" style={{ marginTop: 12 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleApprove}
          color="primary"
          variant="contained"
          disabled={submitting}
        >
          {submitting ? 'Approving…' : 'Approve & assign'}
        </Button>
      </DialogActions>

      <TableAvailabilityDialog
        open={availabilityOpen}
        onClose={() => setAvailabilityOpen(false)}
        date={reservation.reservedAt?.slice(0, 10) || ''}
        reservedAt={
          reservation.reservedAt
            ? reservation.reservedAt.slice(0, 16).replace(' ', 'T')
            : ''
        }
        durationMinutes={reservation.durationMinutes || 120}
      />
    </Dialog>
  );
};
